import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Package,
  Search,
  Filter,
  Plus,
  Star,
  Eye,
  Edit,
  Trash2,
  Grid3X3,
  List,
  ArrowUpDown,
  Tag,
  ImageIcon } from
'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

import { useSearchParams } from 'react-router-dom';

export const EcommerceProducts = () => {const { t } = useTranslation("ecommerce");
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get('store') || '1'; // Default to 1 for demo if no store selected
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchProducts();
  }, [storeId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/ecommerce/stores/${storeId}/products`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const categories = [...new Set(products.map((p) => p.category))];

  const filteredProducts = products.
  filter((p) => categoryFilter === 'all' || p.category === categoryFilter).
  filter((p) => searchQuery === '' ||
  p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  p.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  ).
  sort((a, b) => {
    switch (sortBy) {
      case 'price_low':return (a.sale_price || a.price) - (b.sale_price || b.price);
      case 'price_high':return (b.sale_price || b.price) - (a.sale_price || a.price);
      case 'name':return a.name.localeCompare(b.name);
      case 'stock':return b.stock - a.stock;
      default:return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="ecommerce-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <Package className="h-7 w-7 text-white" />
            </div>{t("products", "Products")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageProductsPublished", "Manage products published to your stores")}

          </p>
        </div>
        <Button className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
          <Plus className="h-4 w-4" />{t("addProduct", "Add Product")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalProducts", "Total Products")}</p>
            <p className="text-2xl font-bold">{products.length}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("featured", "Featured")}</p>
            <p className="text-2xl font-bold text-amber-600">{products.filter((p) => p.is_featured).length}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("onSale", "On Sale")}</p>
            <p className="text-2xl font-bold text-red-600">{products.filter((p) => p.sale_price).length}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("lowStock", "Low Stock")}</p>
            <p className="text-2xl font-bold text-orange-600">{products.filter((p) => p.stock < 100).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search products by name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allCategories", "All Categories")}</SelectItem>
                {categories.map((cat) =>
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">{t("featured", "Featured")}</SelectItem>
                <SelectItem value="name">{t("nameAz", "Name A-Z")}</SelectItem>
                <SelectItem value="price_low">{t("priceLowTo", "Price: Low to High")}</SelectItem>
                <SelectItem value="price_high">{t("priceHighTo", "Price: High to Low")}</SelectItem>
                <SelectItem value="stock">{t("stockLevel", "Stock Level")}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}>
                
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}>
                
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid/List */}
      {viewMode === 'grid' ?
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProducts.map((product) =>
        <Card key={product.id} className="group overflow-hidden card-elevated hover:shadow-xl transition-all duration-300">
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden bg-muted">
                <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            

                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {product.is_featured &&
              <Badge className="gap-1 bg-amber-500 text-white">
                      <Star className="h-3 w-3" />{t("featured", "Featured")}

              </Badge>
              }
                  {product.sale_price &&
              <Badge className="bg-red-500 text-white">{t("sale", "Sale")}

              </Badge>
              }
                </div>

                {/* Quick Actions */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                  <Button size="icon" variant="secondary" className="h-9 w-9">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="secondary" className="h-9 w-9">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Product Info */}
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <Badge variant="outline" className="mb-2 text-xs">
                      {product.category}
                    </Badge>
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">{t("sku", "SKU:")}{product.sku}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-2 mt-3">
                  {product.sale_price ?
              <>
                      <span className="font-bold text-lg text-red-600">
                        {formatCurrency(product.sale_price)}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        {formatCurrency(product.price)}
                      </span>
                    </> :

              <span className="font-bold text-lg">
                      {formatCurrency(product.price)}
                    </span>
              }
                </div>

                {/* Stats Row */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                    <span>{product.rating}</span>
                    <span>({product.reviews})</span>
                  </div>
                  <div className={`font-medium ${product.stock < 100 ? 'text-orange-600' : 'text-emerald-600'}`}>
                    {product.stock}{t("inStock", "in stock")}
              </div>
                </div>
              </CardContent>
            </Card>
        )}
        </div> :

      <Card className="card-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-medium">{t("product", "Product")}</th>
                  <th className="py-3 px-4 text-left text-sm font-medium">{t("sku", "SKU")}</th>
                  <th className="py-3 px-4 text-left text-sm font-medium">{t("category", "Category")}</th>
                  <th className="py-3 px-4 text-right text-sm font-medium">{t("price", "Price")}</th>
                  <th className="py-3 px-4 text-center text-sm font-medium">{t("stock", "Stock")}</th>
                  <th className="py-3 px-4 text-center text-sm font-medium">{t("rating", "Rating")}</th>
                  <th className="py-3 px-4 text-center text-sm font-medium">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProducts.map((product) =>
              <tr key={product.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <img
                      src={product.image}
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded-lg" />
                    
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <div className="flex gap-1 mt-1">
                            {product.is_featured &&
                        <Badge className="text-xs bg-amber-100 text-amber-700">{t("featured", "Featured")}</Badge>
                        }
                            {product.sale_price &&
                        <Badge className="text-xs bg-red-100 text-red-700">{t("sale", "Sale")}</Badge>
                        }
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">{product.sku}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline">{product.category}</Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {product.sale_price ?
                  <div>
                          <p className="font-bold text-red-600">{formatCurrency(product.sale_price)}</p>
                          <p className="text-xs text-muted-foreground line-through">{formatCurrency(product.price)}</p>
                        </div> :

                  <p className="font-bold">{formatCurrency(product.price)}</p>
                  }
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-medium ${product.stock < 100 ? 'text-orange-600' : 'text-emerald-600'}`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        <span>{product.rating}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>
        </Card>
      }

      {filteredProducts.length === 0 &&
      <Card className="card-elevated">
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t("noProductsFound", "No products found")}</p>
          </CardContent>
        </Card>
      }
    </div>);

};

export default EcommerceProducts;